/**
 * 任务结果网格组件
 * 用于展示批量任务的子任务结果
 * 支持原图/生成图对比、失败重试、选择功能
 */

import type { ReactElement } from 'react'
import { useState, useCallback } from 'react'
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  RotateCcw,
  Check,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Ban,
} from 'lucide-react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import type { BatchTaskResultItem } from '@/types/task'
import { TASK_RESULT_STATUS } from '@/types/task'

/**
 * 组件属性
 */
interface TaskResultGridProps {
  /** 子任务结果列表 */
  results: BatchTaskResultItem[]
  /** 选中的任务 ID 列表 */
  selectedIds: string[]
  /** 选择变化回调 */
  onSelectionChange: (ids: string[]) => void
  /** 重试单个任务回调 */
  onRetry: (taskId: string) => void
  /** 是否正在重试 */
  isRetrying?: boolean
  /** 当前页起始索引（用于显示序号） */
  startIndex?: number
  /** 是否处于选择模式（用于选择下载图片） */
  selectionMode?: boolean
  /** 提示词数量（用于判断是否显示提示词信息） */
  promptCount?: number
}

/**
 * 获取状态图标和颜色配置
 */
function getStatusConfig(status: string): {
  icon: typeof CheckCircle
  className: string
  text: string
  iconSpin?: boolean
} {
  switch (status) {
    case TASK_RESULT_STATUS.QUEUED:
      return {
        icon: Clock,
        className: 'text-amber-500',
        text: '排队中',
      }
    case TASK_RESULT_STATUS.RUNNING:
      return {
        icon: Loader2,
        className: 'text-blue-500',
        text: '生成中',
        iconSpin: true,
      }
    case TASK_RESULT_STATUS.COMPLETED:
      return {
        icon: CheckCircle,
        className: 'text-emerald-500',
        text: '完成',
      }
    case TASK_RESULT_STATUS.FAILED:
      return {
        icon: XCircle,
        className: 'text-red-500',
        text: '失败',
      }
    case TASK_RESULT_STATUS.CANCELLED:
      return {
        icon: Ban,
        className: 'text-slate-500',
        text: '已取消',
      }
    default:
      return {
        icon: Clock,
        className: 'text-muted-foreground',
        text: '未知',
      }
  }
}

/**
 * 单个结果卡片属性
 */
interface ResultCardProps {
  /** 子任务结果 */
  result: BatchTaskResultItem
  /** 显示序号（从 1 开始） */
  displayIndex: number
  /** 是否选中 */
  isSelected: boolean
  /** 选择变化回调 */
  onToggleSelect: () => void
  /** 重试回调 */
  onRetry: () => void
  /** 是否正在重试 */
  isRetrying: boolean
  /** 是否处于选择模式 */
  selectionMode: boolean
  /** 是否显示提示词信息 */
  showPromptInfo: boolean
}

/**
 * 单个结果卡片组件
 */
function ResultCard({
  result,
  displayIndex,
  isSelected,
  onToggleSelect,
  onRetry,
  isRetrying,
  selectionMode,
  showPromptInfo,
}: ResultCardProps): ReactElement {
  const [originalLoaded, setOriginalLoaded] = useState(false)
  const [generatedLoaded, setGeneratedLoaded] = useState(false)
  const [errorExpanded, setErrorExpanded] = useState(false)

  const statusConfig = getStatusConfig(result.status)
  const StatusIcon = statusConfig.icon
  const isFailed = result.status === TASK_RESULT_STATUS.FAILED
  const isCompleted = result.status === TASK_RESULT_STATUS.COMPLETED
  const isCancelled = result.status === TASK_RESULT_STATUS.CANCELLED
  const isProcessing =
    result.status === TASK_RESULT_STATUS.QUEUED ||
    result.status === TASK_RESULT_STATUS.RUNNING
  const generatedOutputUrl = result.output_urls?.[0] ?? result.output_url
  const outputCount = result.output_count ?? result.output_urls?.length ?? 0
  const originalPreviewEnabled = !selectionMode && originalLoaded
  const generatedPreviewEnabled = !selectionMode && isCompleted && generatedLoaded

  /**
   * 处理卡片点击事件
   * 选择模式下点击卡片即可切换选中状态
   */
  const handleCardClick = (): void => {
    // 选择模式下已完成的图片可以通过点击卡片选中
    if (selectionMode && isCompleted) {
      onToggleSelect()
    }
  }

  return (
    <div
      className={cn(
        'group relative rounded-xl border bg-card p-3 transition-all',
        isSelected && 'border-primary ring-1 ring-primary',
        isFailed && 'border-red-500/30 bg-red-500/5',
        isCancelled && 'border-slate-500/30 bg-slate-500/5',
        selectionMode && isCompleted && 'cursor-pointer hover:border-primary/50'
      )}
      onClick={handleCardClick}
    >
      {/* 选中指示器（选择模式下已完成的图片显示） */}
      {selectionMode && isCompleted && (
        <div className="absolute left-2 top-2 z-10">
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-muted-foreground/50 bg-background'
            )}
          >
            {isSelected && <Check className="h-3 w-3" />}
          </div>
        </div>
      )}
      {/* 失败状态的复选框 */}
      {isFailed && (
        <div className="absolute left-2 top-2 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="h-5 w-5 border-2"
          />
        </div>
      )}

      {/* 状态栏 */}
      <div className="mb-2 flex items-center justify-end">
        <div className="flex items-center gap-1">
          <StatusIcon
            className={cn(
              'h-4 w-4',
              statusConfig.className,
              statusConfig.iconSpin === true && 'animate-spin'
            )}
          />
          <span className={cn('text-xs font-medium', statusConfig.className)}>
            {statusConfig.text}
          </span>
        </div>
      </div>

      {/* 提示词信息（当有多个提示词时显示） */}
      {showPromptInfo && result.prompt !== undefined && result.prompt !== '' && (
        <div className="mb-2 rounded-md bg-muted/50 px-2 py-1.5">
          <div className="flex items-center gap-1 text-xs">
            <span className="shrink-0 font-medium text-primary">
              提示词 #{(result.prompt_index ?? 0) + 1}
            </span>
          </div>
          <p
            className="mt-0.5 line-clamp-2 text-xs text-muted-foreground"
            title={result.prompt}
          >
            {result.prompt}
          </p>
        </div>
      )}

      {/* 图片对比区域 */}
      <div className="flex items-center gap-2">
        {/* 原始图 */}
        <div className={cn('relative flex-1 overflow-hidden rounded-lg bg-muted', originalPreviewEnabled && 'cursor-pointer')}>
          <div className="aspect-square">
            {!originalLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {/* react-photo-view 仅在首次挂载时读取 triggers，需在开关变化时重建组件 */}
            <PhotoView
              key={originalPreviewEnabled ? 'original-preview-enabled' : 'original-preview-disabled'}
              src={result.input_url}
              triggers={originalPreviewEnabled ? ['onClick'] : []}
            >
              <img
                src={result.input_url}
                alt="原始图"
                className={cn(
                  'h-full w-full object-cover transition-opacity',
                  originalLoaded ? 'opacity-100' : 'opacity-0'
                )}
                onLoad={(): void => {
                  setOriginalLoaded(true)
                }}
              />
            </PhotoView>
          </div>
          {/* 序号角标 */}
          <div className="absolute right-1 top-1 flex h-5 min-w-5 items-center justify-center rounded bg-black/60 px-1 text-[10px] font-medium text-white">
            {displayIndex}
          </div>
          <div className="absolute bottom-1 left-1 rounded bg-black/50 px-1 py-0.5 text-[10px] text-white">
            原图
          </div>
        </div>

        {/* 箭头 */}
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />

        {/* 生成图 */}
        <div className={cn('relative flex-1 overflow-hidden rounded-lg bg-muted', generatedPreviewEnabled && 'cursor-pointer')}>
          <div className="aspect-square">
            {isCompleted && generatedOutputUrl !== null ? (
              <>
                {!generatedLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
                <PhotoView
                  key={generatedPreviewEnabled ? 'generated-preview-enabled' : 'generated-preview-disabled'}
                  src={generatedOutputUrl}
                  triggers={generatedPreviewEnabled ? ['onClick'] : []}
                >
                  <img
                    src={generatedOutputUrl}
                    alt="生成图"
                    className={cn(
                      'h-full w-full object-cover transition-opacity',
                      generatedLoaded ? 'opacity-100' : 'opacity-0'
                    )}
                    onLoad={(): void => {
                      setGeneratedLoaded(true)
                    }}
                  />
                </PhotoView>
              </>
            ) : isProcessing ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                {result.status === TASK_RESULT_STATUS.RUNNING ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    <span className="text-xs text-blue-600 dark:text-blue-400">生成中</span>
                  </>
                ) : (
                  <>
                    <Clock className="h-6 w-6 text-amber-500" />
                    <span className="text-xs text-amber-600 dark:text-amber-400">排队中</span>
                  </>
                )}
              </div>
            ) : isFailed ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2">
                <XCircle className="h-6 w-6 shrink-0 text-red-500" />
                {result.error !== null && result.error !== '' && result.error.length > 30 ? (
                  <button
                    type="button"
                    onClick={(): void => {
                      setErrorExpanded(!errorExpanded)
                    }}
                    className="flex items-center gap-1 text-[10px] text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <span className="font-medium">点击查看错误</span>
                    {errorExpanded ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                ) : (
                  <span className="line-clamp-3 text-center text-[10px] text-red-600 dark:text-red-400">
                    {result.error ?? '处理失败'}
                  </span>
                )}
              </div>
            ) : isCancelled ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                <Ban className="h-6 w-6 text-slate-500" />
                <span className="text-xs text-slate-600 dark:text-slate-400">已取消</span>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs text-muted-foreground">等待中</span>
              </div>
            )}
          </div>
          {isCompleted && (
            <div className="absolute bottom-1 left-1 rounded bg-black/50 px-1 py-0.5 text-[10px] text-white">
              {outputCount > 1 ? `生成 1/${String(outputCount)}` : '生成'}
            </div>
          )}
        </div>
      </div>

      {/* 错误详情展开区域 */}
      {isFailed && errorExpanded && result.error !== null && result.error !== '' && (
        <div className="mt-2">
          <Alert variant="destructive" className="p-2">
            <AlertCircle className="h-3.5 w-3.5" />
            <AlertDescription className="text-xs leading-relaxed break-words">
              {result.error}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* 失败时显示重试按钮 */}
      {isFailed && (
        <div className="mt-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full gap-1 text-xs"
            onClick={onRetry}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                重试中
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3" />
                点击重试
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * 任务结果网格组件
 * @param props - 组件属性
 * @returns 任务结果网格 ReactElement
 */
export function TaskResultGrid({
  results,
  selectedIds,
  onSelectionChange,
  onRetry,
  isRetrying = false,
  startIndex = 0,
  selectionMode = false,
  promptCount = 1,
}: TaskResultGridProps): ReactElement {
  /**
   * 切换单个任务的选中状态
   */
  const handleToggleSelect = useCallback(
    (taskId: string): void => {
      if (selectedIds.includes(taskId)) {
        onSelectionChange(selectedIds.filter((id) => id !== taskId))
      } else {
        onSelectionChange([...selectedIds, taskId])
      }
    },
    [selectedIds, onSelectionChange]
  )

  /**
   * 处理单个任务重试
   */
  const handleRetry = useCallback(
    (taskId: string): void => {
      onRetry(taskId)
    },
    [onRetry]
  )

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground">暂无任务结果</p>
      </div>
    )
  }

  return (
    <PhotoProvider
      maskOpacity={0.65}
      photoClassName="object-contain scale-[0.78]"
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {results.map((result, index) => (
          <ResultCard
            key={result.task_id}
            result={result}
            displayIndex={startIndex + index + 1}
            isSelected={selectedIds.includes(result.task_id)}
            onToggleSelect={(): void => {
              handleToggleSelect(result.task_id)
            }}
            onRetry={(): void => {
              handleRetry(result.task_id)
            }}
            isRetrying={isRetrying}
            selectionMode={selectionMode}
            showPromptInfo={promptCount > 1}
          />
        ))}
      </div>
    </PhotoProvider>
  )
}

/**
 * 全选失败按钮组件属性
 */
interface SelectAllFailedButtonProps {
  /** 所有失败的任务 ID 列表 */
  failedIds: string[]
  /** 已选中的任务 ID 列表 */
  selectedIds: string[]
  /** 选择变化回调 */
  onSelectionChange: (ids: string[]) => void
}

/**
 * 全选失败按钮组件
 */
export function SelectAllFailedButton({
  failedIds,
  selectedIds,
  onSelectionChange,
}: SelectAllFailedButtonProps): ReactElement {
  const allSelected =
    failedIds.length > 0 &&
    failedIds.every((id) => selectedIds.includes(id))

  const handleClick = useCallback((): void => {
    if (allSelected) {
      // 取消选择所有失败的
      onSelectionChange(selectedIds.filter((id) => !failedIds.includes(id)))
    } else {
      // 选择所有失败的
      const newSelected = [...new Set([...selectedIds, ...failedIds])]
      onSelectionChange(newSelected)
    }
  }, [allSelected, failedIds, selectedIds, onSelectionChange])

  if (failedIds.length === 0) {
    return <></>
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      onClick={handleClick}
    >
      {allSelected ? (
        <>
          <XCircle className="h-4 w-4" />
          取消全选
        </>
      ) : (
        <>
          <Check className="h-4 w-4" />
          全选失败 ({failedIds.length})
        </>
      )}
    </Button>
  )
}
